import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { processQRCode } from '../services/qrService';

const QRScanHandler: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<'processing' | 'success' | 'error' | 'syncing'>('processing');
  const [qrNumber, setQrNumber] = useState<string | null>(null);
  const [emoji, setEmoji] = useState<string | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnlineStatus = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);
    
    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, []);

  useEffect(() => {
    const processScan = async () => {
      try {
        // Extract token from URL hash
        const hash = location.hash;
        
        if (!hash || hash.length <= 1) {
          setStatus('error');
          setErrorMessage('Kein QR-Code-Token in der URL gefunden.');
          return;
        }

        // Remove the leading # from hash
        const token = hash.substring(1);
        
        // Construct the full QR code content (base URL + # + token)
        const qrContent = `${window.location.origin}/scan#${token}`;
        
        // Process the QR code (this works offline and queues for sync)
        const result = await processQRCode(qrContent);
        
        if (result.success && result.qrNumber) {
          setQrNumber(result.qrNumber);
          setEmoji(result.emoji);
          setStatus(isOnline ? 'success' : 'syncing');
          
          // Redirect to home after 3 seconds
          setTimeout(() => {
            navigate('/');
          }, 3000);
        } else {
          setStatus('error');
          setErrorMessage('Ungültiger QR-Code. Der Token konnte nicht validiert werden.');
        }
      } catch (error) {
        console.error('Error processing QR scan from URL:', error);
        setStatus('error');
        setErrorMessage('Fehler beim Verarbeiten des QR-Codes.');
      }
    };

    processScan();
  }, [location.hash, navigate, isOnline]);

  return (
    <div className="qr-scan-handler">
      <div className="scan-status-card">
        {status === 'processing' && (
          <>
            <div className="spinner"></div>
            <h2>QR-Code wird verarbeitet...</h2>
            <p>Bitte warten Sie einen Moment.</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="success-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <h2>QR-Code erfolgreich gescannt!</h2>
            <p className="qr-number">Rätsel #{qrNumber}{emoji && ` ${emoji}`}</p>
            <p className="redirect-info">Du wirst in Kürze weitergeleitet...</p>
          </>
        )}
        
        {status === 'syncing' && (
          <>
            <div className="sync-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="rotating">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
            </div>
            <h2>QR-Code gespeichert!</h2>
            <p className="qr-number">Rätsel #{qrNumber}{emoji && ` ${emoji}`}</p>
            <div className="offline-notice">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="1" y1="1" x2="23" y2="23"></line>
                <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"></path>
                <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"></path>
                <path d="M10.71 5.05A16 16 0 0 1 22.58 9"></path>
                <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"></path>
                <path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path>
                <line x1="12" y1="20" x2="12.01" y2="20"></line>
              </svg>
              <span>Offline - Wird synchronisiert, sobald du online bist</span>
            </div>
            <p className="redirect-info">Du wirst in Kürze weitergeleitet...</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="error-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
            </div>
            <h2>Fehler beim Scannen</h2>
            <p className="error-message">{errorMessage}</p>
            <button 
              className="back-button"
              onClick={() => navigate('/')}
            >
              Zurück zur Startseite
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default QRScanHandler;
